import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardBody, Button } from "reactstrap";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { showToastMessage } from "common/service/CommonService";
import AddEditCheckListModal from "modules/Library/Checklist/components/AddEditCheckListModal";
import TruncatedText from "Components/Common/TruncatedText";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import styles from "../css/addstudy.module.scss";
import {
  getActiveChecklistTypes,
  getSelectedTypeLabel,
  executeCopyFromPeriod,
  checkliststepcolumns,
  normalizeStudyChecklists,
} from "../helpers/studyconstant";

interface ChecklistAssignmentStepProps {
  control?: any;
  setValue?: any;
  watch?: any;
  rawChecklistData?: any[];
  checklistLoading?: boolean;
  existingStudyChecklists?: any[];
  existingMode?: string;
}

// Reusable scroll wrapper component for horizontal chip rows
const HorizontalChipBar: React.FC<{ children: React.ReactNode; id: string }> = ({ children, id }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
  };

  useEffect(() => {
    checkScroll();
    const el = containerRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }
    return () => {
      if (el) el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [children]);

  const scroll = (direction: "left" | "right") => {
    const el = containerRef.current;
    if (!el) return;
    const firstChip = el.firstElementChild as HTMLElement | null;
    const scrollAmount = firstChip ? firstChip.offsetWidth + 8 : 150;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <div className={`card border shadow-sm ${styles.horizontalChipBar}`}>
      <button
        type="button"
        disabled={!canScrollLeft}
        onClick={() => scroll("left")}
        className={styles.scrollArrowBtn}
      >
        <i className="ri-arrow-left-s-line" />
      </button>

      <div id={id} ref={containerRef} className={styles.chipScrollContainer}>
        {children}
      </div>

      <button
        type="button"
        disabled={!canScrollRight}
        onClick={() => scroll("right")}
        className={styles.scrollArrowBtn}
      >
        <i className="ri-arrow-right-s-line" />
      </button>
    </div>
  );
};

const ChecklistAssignmentStep: React.FC<ChecklistAssignmentStepProps> = ({
  control,
  setValue,
  watch,
  rawChecklistData,
  checklistLoading,
  existingStudyChecklists,
}) => {
  const mode = "periodspecific";

  const watchedNumPeriods = watch ? watch("numberofperiods") : 4;
  const numPeriods = Number(watchedNumPeriods) || 1;

  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const [selectedType, setSelectedType] = useState<number>();

  // Custom modified or added checklists per period/type context
  const [customChecklists, setCustomChecklists] = useState<Record<string, any[]>>({});

  // Add / Edit Modal State using shared AddEditCheckListModal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);

  // Hidden checklist type chips (scoped per period/common mode context)
  const [hiddenTypeValuesState, setHiddenTypeValuesState] = useState<Record<string, number[]>>({});
  const [showAddTypeDropdown, setShowAddTypeDropdown] = useState(false);
  const addTypeDropdownRef = useRef<HTMLDivElement>(null);

  // Current hidden types context key: e.g. "p1", "p2" or "common"
  const hiddenContextKey = mode === "periodspecific" ? `p${selectedPeriod}` : "common";

  const currentHiddenSet = useMemo(() => {
    const list = hiddenTypeValuesState[hiddenContextKey] || [];
    return new Set<number>(list);
  }, [hiddenTypeValuesState, hiddenContextKey]);

  // Period-to-Period copy dropdown state
  const [showCopyDropdown, setShowCopyDropdown] = useState(false);
  const copyDropdownRef = useRef<HTMLDivElement>(null);

  const rawApiData: any[] = rawChecklistData || [];

  // Detect if Inclusion or Exclusion criteria exist in the raw checklist data
  const hasInclusionExclusionCriteria = useMemo(() => {
    if (!Array.isArray(rawApiData)) return false;
    return rawApiData.some((item: any) => {
      const text = String(
        item.checklisttypetext || item.checklisttypename || item.checklisttype_text || ""
      ).toLowerCase();
      return text.includes("inclusion") || text.includes("exclusion");
    });
  }, [rawApiData]);

  const [selectedScope, setSelectedScope] = useState<"pre_enrollment" | "period_based">("pre_enrollment");

  // Helper to test if a type is Pre-Enrollment related
  const isPreEnrollmentType = (text: string) => {
    const t = text.toLowerCase();
    return (
      t.includes("inclusion") ||
      t.includes("exclusion") ||
      t.includes("pre enrollment") ||
      t.includes("pre-enrollment")
    );
  };

  // Extract dynamic checklist types using helper
  const activeChecklistTypes = useMemo(() => {
    return getActiveChecklistTypes(rawApiData);
  }, [rawApiData]);

  // Scope-filtered checklist types
  const scopeChecklistTypes = useMemo(() => {
    if (!hasInclusionExclusionCriteria) {
      return activeChecklistTypes;
    }
    return activeChecklistTypes.filter((t) => {
      const isPre = isPreEnrollmentType(t.label);
      return selectedScope === "pre_enrollment" ? isPre : !isPre;
    });
  }, [activeChecklistTypes, hasInclusionExclusionCriteria, selectedScope]);

  const visibleChecklistTypes = useMemo(() => {
    return scopeChecklistTypes.filter((t) => !currentHiddenSet.has(t.value));
  }, [scopeChecklistTypes, currentHiddenSet]);

  const hiddenChecklistTypes = useMemo(() => {
    return scopeChecklistTypes.filter((t) => currentHiddenSet.has(t.value));
  }, [scopeChecklistTypes, currentHiddenSet]);

  const selectedTypeLabel = useMemo(() => {
    return getSelectedTypeLabel(activeChecklistTypes, selectedType);
  }, [activeChecklistTypes, selectedType]);

  // Auto-select first visible checklist type when scope or visible types change
  useEffect(() => {
    if (visibleChecklistTypes.length > 0) {
      if (!selectedType || !visibleChecklistTypes.some((t) => Number(t.value) === Number(selectedType))) {
        setSelectedType(Number(visibleChecklistTypes[0].value));
      }
    } else {
      if (selectedType !== undefined) {
        setSelectedType(undefined);
      }
    }
  }, [visibleChecklistTypes, selectedType, selectedScope]);

  // If hidden type is the selected type, auto-switch to immediate previous or first visible
  useEffect(() => {
    if (selectedType && currentHiddenSet.has(Number(selectedType))) {
      if (visibleChecklistTypes.length > 0) {
        const selIdx = activeChecklistTypes.findIndex((t) => Number(t.value) === Number(selectedType));
        const prevVisible = [...activeChecklistTypes.slice(0, selIdx)]
          .reverse()
          .find((t) => !currentHiddenSet.has(t.value));
        if (prevVisible) {
          setSelectedType(Number(prevVisible.value));
        } else {
          setSelectedType(Number(visibleChecklistTypes[0].value));
        }
      } else {
        setSelectedType(undefined);
      }
    }
  }, [currentHiddenSet, activeChecklistTypes, visibleChecklistTypes, selectedType]);

  const periodList = useMemo(() => {
    return Array.from({ length: Math.max(1, numPeriods) }, (_, i) => i + 1);
  }, [numPeriods]);

  useEffect(() => {
    if (selectedPeriod > numPeriods) {
      setSelectedPeriod(1);
    }
  }, [numPeriods, selectedPeriod]);

  // Current key for data storage: e.g. "p1_373" or "common_373"
  const currentKey = useMemo(() => {
    if (hasInclusionExclusionCriteria && selectedScope === "pre_enrollment") {
      return `p1_${selectedType}`;
    }
    return mode === "periodspecific" ? `p${selectedPeriod}_${selectedType}` : `common_${selectedType}`;
  }, [mode, selectedPeriod, selectedType, hasInclusionExclusionCriteria, selectedScope]);

  const [assignedState, setAssignedState] = useState<Record<string, Record<number, boolean>>>({});

  const isEditMode = existingStudyChecklists !== undefined;

  // Build a fast lookup map of saved items from existingStudyChecklists (edit mode)
  const existingSavedMap = useMemo(() => {
    const flatItems = normalizeStudyChecklists(existingStudyChecklists);
    if (!flatItems.length) return null;

    const map: Record<
      string,
      { masterIds: Set<number>; customItems: any[]; savedMasterMap: Map<number, any> }
    > = {};

    const ensureKey = (key: string) => {
      if (!map[key]) {
        map[key] = {
          masterIds: new Set<number>(),
          customItems: [],
          savedMasterMap: new Map<number, any>(),
        };
      }
      return map[key];
    };

    flatItems.forEach((item: any) => {
      const periodVal = Number(item.period_sort || item.periodid || item.period_no) || 1;
      const typeVal = Number(item.checklisttype);

      const periodKey = `p${periodVal}_${typeVal}`;
      const commonKey = `common_${typeVal}`;

      const keysToPopulate = [periodKey, commonKey];

      keysToPopulate.forEach((contextKey) => {
        const target = ensureKey(contextKey);
        const hasMaster = item.masterchecklistid != null && Number(item.masterchecklistid) !== 0;

        if (hasMaster) {
          const masterId = Number(item.masterchecklistid);
          if (!target.masterIds.has(masterId)) {
            target.masterIds.add(masterId);
            target.savedMasterMap.set(masterId, item);
          }
        } else {
          const qClean = String(item.question || "").trim().toLowerCase();
          const alreadyExists = target.customItems.some(
            (c) => String(c.question || "").trim().toLowerCase() === qClean
          );

          if (!alreadyExists) {
            const customTrackingId = Number(item.studychecklistid) || item.id || (Date.now() + Math.random());
            target.customItems.push({
              studychecklistid: Number(item.studychecklistid) || null,
              masterchecklistid: null,
              id: customTrackingId,
              question: item.question,
              checklisttype: typeVal,
              checklisttypetext: item.checklisttypetext || "",
              checklistcategory: item.checklistcategory || null,
              checklistcategorytext: item.checklistcategorytext || "",
              isrequired: !!item.isrequired,
              assigned: true,
              isCustom: true,
              displayorder: item.displayorder || "01",
            });
          }
        }
      });
    });

    return map;
  }, [existingStudyChecklists]);

  const handleToggleAssign = (id: number) => {
    setAssignedState((prev) => {
      const currentMap = prev[currentKey] || {};
      const currentVal = currentChecklists.find((i: any) => i.id === id)?.assigned;
      return {
        ...prev,
        [currentKey]: {
          ...currentMap,
          [id]: !currentVal,
        },
      };
    });
  };

  const currentChecklists = useMemo(() => {
    if (!selectedType || currentHiddenSet.has(Number(selectedType))) return [];

    const currentMap = assignedState[currentKey] || {};
    const savedContext = existingSavedMap ? existingSavedMap[currentKey] : null;

    let list: any[] = [];

    if (customChecklists[currentKey]) {
      list = customChecklists[currentKey];
    } else {
      const masterItemsForType = Array.isArray(rawApiData)
        ? rawApiData.filter((item: any) => Number(item.checklisttype) === Number(selectedType))
        : [];

      const mappedMasterItems = masterItemsForType.map((masterItem: any) => {
        const masterId = Number(masterItem.masterchecklistid || masterItem.id);
        const savedItem = savedContext?.savedMasterMap.get(masterId);

        if (savedItem) {
          return {
            ...masterItem,
            studychecklistid: Number(savedItem.studychecklistid) || null,
            question: savedItem.question || masterItem.question,
            isrequired: savedItem.isrequired !== undefined ? !!savedItem.isrequired : !!masterItem.isrequired,
            displayorder: savedItem.displayorder || masterItem.displayorder,
            assigned: true,
          };
        }

        return {
          ...masterItem,
          studychecklistid: null,
          assigned: !isEditMode,
        };
      });

      const customItems = savedContext?.customItems || [];

      list = [...mappedMasterItems, ...customItems];

      list.sort((a, b) => {
        const orderA = Number(a.displayorder) || 999;
        const orderB = Number(b.displayorder) || 999;
        return orderA - orderB;
      });
    }

    return list.map((item: any, idx: number) => {
      const trackingId = item.id || item.masterchecklistid || idx + 1;

      let isAssigned = item.assigned;
      if (currentMap[trackingId] !== undefined) {
        isAssigned = currentMap[trackingId];
      } else if (isEditMode) {
        if (item.isCustom) {
          isAssigned = true;
        } else {
          const masterId = Number(item.masterchecklistid || item.id);
          isAssigned = savedContext?.masterIds.has(masterId) ?? false;
        }
      } else {
        if (item.assigned !== undefined) {
          isAssigned = item.assigned;
        } else {
          isAssigned = true;
        }
      }

      const displayOrderVal = item.displayorder !== undefined && item.displayorder !== null
        ? String(item.displayorder).padStart(2, "0")
        : String(idx + 1).padStart(2, "0");

      return {
        ...item,
        id: trackingId,
        masterchecklistid: item.masterchecklistid !== undefined ? item.masterchecklistid : (item.isCustom ? null : trackingId),
        assigned: isAssigned,
        displayorder: displayOrderVal,
      };
    });
  }, [assignedState, currentKey, customChecklists, rawApiData, selectedType, existingSavedMap, isEditMode]);

  const isAllAssigned = useMemo(() => {
    if (currentChecklists.length === 0) return false;
    return currentChecklists.every((item: any) => item.assigned);
  }, [currentChecklists]);

  const handleToggleSelectAll = () => {
    const targetVal = !isAllAssigned;
    setAssignedState((prev) => {
      const currentMap = { ...(prev[currentKey] || {}) };
      currentChecklists.forEach((item: any) => {
        currentMap[item.id] = targetVal;
      });
      return {
        ...prev,
        [currentKey]: currentMap,
      };
    });
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeIndex = currentChecklists.findIndex((item: any) => item.id === active.id);
    const overIndex = currentChecklists.findIndex((item: any) => item.id === over.id);

    if (activeIndex === -1 || overIndex === -1) return;

    const reorderedList = arrayMove(currentChecklists, activeIndex, overIndex).map(
      (item: any, idx: number) => ({
        ...item,
        displayorder: String(idx + 1).padStart(2, "0"),
      })
    );

    setCustomChecklists((prev) => ({
      ...prev,
      [currentKey]: reorderedList,
    }));
  };

  // Count assigned items per checklist type
  const getTypeCount = (typeVal: number) => {
    const key = (hasInclusionExclusionCriteria && selectedScope === "pre_enrollment")
      ? `p1_${typeVal}`
      : (mode === "periodspecific" ? `p${selectedPeriod}_${typeVal}` : `common_${typeVal}`);
    const currentMap = assignedState[key] || {};
    const savedContext = existingSavedMap ? existingSavedMap[key] : null;

    let items: any[] = [];
    if (customChecklists[key]) {
      items = customChecklists[key];
    } else {
      const masterItems = Array.isArray(rawApiData)
        ? rawApiData.filter((i: any) => Number(i.checklisttype) === Number(typeVal))
        : [];
      const customItems = savedContext?.customItems || [];
      items = [...masterItems, ...customItems];
    }

    return items.filter((item: any) => {
      const trackingId = item.id || item.masterchecklistid;
      if (currentMap[trackingId] !== undefined) return currentMap[trackingId];
      if (isEditMode) {
        if (item.isCustom) return true;
        const masterId = Number(item.masterchecklistid || item.id);
        return savedContext?.masterIds.has(masterId) ?? false;
      }
      return item.assigned !== undefined ? item.assigned : true;
    }).length;
  };

  const handleHideType = (typeVal: number) => {
    if (Number(selectedType) === Number(typeVal)) {
      const currentIndex = visibleChecklistTypes.findIndex(
        (t) => Number(t.value) === Number(typeVal)
      );
      if (currentIndex > 0) {
        setSelectedType(Number(visibleChecklistTypes[currentIndex - 1].value));
      } else if (visibleChecklistTypes.length > 1) {
        setSelectedType(Number(visibleChecklistTypes[1].value));
      } else {
        setSelectedType(undefined);
      }
    }

    setHiddenTypeValuesState((prev) => {
      const currentList = prev[hiddenContextKey] || [];
      if (currentList.includes(typeVal)) return prev;
      return {
        ...prev,
        [hiddenContextKey]: [...currentList, typeVal],
      };
    });
    setShowAddTypeDropdown(false);
  };

  const handleRestoreType = (typeVal: number) => {
    setHiddenTypeValuesState((prev) => {
      const currentList = prev[hiddenContextKey] || [];
      return {
        ...prev,
        [hiddenContextKey]: currentList.filter((v) => v !== typeVal),
      };
    });
    setShowAddTypeDropdown(false);
    setSelectedType(typeVal);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (addTypeDropdownRef.current && !addTypeDropdownRef.current.contains(e.target as Node)) {
        setShowAddTypeDropdown(false);
      }
      if (copyDropdownRef.current && !copyDropdownRef.current.contains(e.target as Node)) {
        setShowCopyDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Helper to compute actual current items and assigned states for any period and checklist type
  const getItemsForPeriod = (periodNum: number, typeVal: number) => {
    const key = `p${periodNum}_${typeVal}`;
    const mapForPeriod = assignedState[key] || {};
    const savedContext = existingSavedMap ? existingSavedMap[key] : null;

    let list: any[] = [];
    if (customChecklists[key]) {
      list = customChecklists[key];
    } else {
      const masterItemsForType = Array.isArray(rawApiData)
        ? rawApiData.filter((item: any) => Number(item.checklisttype) === Number(typeVal))
        : [];

      const mappedMasterItems = masterItemsForType.map((masterItem: any) => {
        const masterId = Number(masterItem.masterchecklistid || masterItem.id);
        const savedItem = savedContext?.savedMasterMap.get(masterId);
        if (savedItem) {
          return {
            ...masterItem,
            studychecklistid: Number(savedItem.studychecklistid) || null,
            question: savedItem.question || masterItem.question,
            isrequired: savedItem.isrequired !== undefined ? !!savedItem.isrequired : !!masterItem.isrequired,
            displayorder: savedItem.displayorder || masterItem.displayorder,
            assigned: true,
          };
        }
        return {
          ...masterItem,
          studychecklistid: null,
          assigned: !isEditMode,
        };
      });

      const customItems = savedContext?.customItems || [];
      list = [...mappedMasterItems, ...customItems];
    }

    return list.map((item: any, idx: number) => {
      const trackingId = item.id || item.masterchecklistid || idx + 1;
      let isAssigned = item.assigned;

      if (mapForPeriod[trackingId] !== undefined) {
        isAssigned = mapForPeriod[trackingId];
      } else if (isEditMode) {
        if (item.isCustom) {
          isAssigned = true;
        } else {
          const masterId = Number(item.masterchecklistid || item.id);
          isAssigned = savedContext?.masterIds.has(masterId) ?? false;
        }
      } else {
        isAssigned = item.assigned !== undefined ? item.assigned : true;
      }

      return {
        ...item,
        id: trackingId,
        assigned: isAssigned,
      };
    });
  };

  // Copy active checklist type items and their checkbox states from chosen period
  const handleCopyFromPeriod = (srcPeriod: number) => {
    if (!selectedType) return;

    const srcItems = getItemsForPeriod(srcPeriod, Number(selectedType));
    if (!srcItems || srcItems.length === 0) {
      showToastMessage("error", `No checklists found in Period ${srcPeriod} for ${selectedTypeLabel}.`);
      setShowCopyDropdown(false);
      return;
    }

    const dstKey = `p${selectedPeriod}_${selectedType}`;

    const newAssignedMap: Record<number, boolean> = {};
    const copiedCustomList: any[] = srcItems.map((item: any, idx: number) => {
      const trackingId = item.isCustom
        ? (Date.now() + idx + Math.random())
        : (item.id || item.masterchecklistid);

      newAssignedMap[trackingId] = !!item.assigned;

      return {
        ...item,
        id: trackingId,
        studychecklistid: null, // Reset so backend treats copied items as new entries for destination period
        assigned: !!item.assigned,
      };
    });

    setCustomChecklists((prev) => ({
      ...prev,
      [dstKey]: copiedCustomList,
    }));

    setAssignedState((prev) => ({
      ...prev,
      [dstKey]: newAssignedMap,
    }));

    // Copy hidden types state from srcPeriod to selectedPeriod
    const srcHiddenList = hiddenTypeValuesState[`p${srcPeriod}`] || [];
    setHiddenTypeValuesState((prev) => ({
      ...prev,
      [`p${selectedPeriod}`]: [...srcHiddenList],
    }));

    const typeText = selectedTypeLabel ? `${selectedTypeLabel} ` : "";
    showToastMessage("success", `Successfully copied ${typeText}checklist from Period ${srcPeriod} to Period ${selectedPeriod}.`);
    setShowCopyDropdown(false);
  };

  // Add / Edit Handlers using shared AddEditCheckListModal
  const handleOpenAddModal = () => {
    document.body.click();
    setEditingItem(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (row: any) => {
    document.body.click();
    setEditingItem(row);
    setModalOpen(true);
  };

  const handleSaveModal = (record: any) => {
    const qClean = String(record.question || "").trim().toLowerCase();

    if (editingItem) {
      const targetId = editingItem.id || editingItem.masterchecklistid;
      const isDuplicate = currentChecklists.some((item: any) => {
        const itemId = item.id || item.masterchecklistid;
        if (itemId === targetId) return false;
        return String(item.question || "").trim().toLowerCase() === qClean;
      });

      if (isDuplicate) {
        showToastMessage("error", "Checklist already exists");
        return;
      }

      const updatedList = currentChecklists.map((item) =>
        (item.id || item.masterchecklistid) === targetId
          ? {
            ...item,
            question: record.question,
            isrequired: record.isrequired,
            checklisttype: record.checklisttype || selectedType,
          }
          : item
      );
      setCustomChecklists((prev) => ({
        ...prev,
        [currentKey]: updatedList,
      }));
      showToastMessage("success", "Checklist updated");
    } else {
      const isDuplicate = currentChecklists.some((item: any) => {
        return String(item.question || "").trim().toLowerCase() === qClean;
      });

      if (isDuplicate) {
        showToastMessage("error", "Checklist already exists");
        return;
      }

      const typeVal = Number(record.checklisttype || selectedType);
      const existingItem = currentChecklists.find((i: any) => Number(i.checklisttype) === typeVal)
        || (Array.isArray(rawChecklistData) ? rawChecklistData.find((i: any) => Number(i.checklisttype) === typeVal) : null);
      const categoryVal = record.checklistcategory || existingItem?.checklistcategory || null;

      // Compute MAX display order among rawChecklistData (selectall response) and currentChecklists
      const allOrders: number[] = [];

      if (Array.isArray(currentChecklists)) {
        currentChecklists.forEach((i: any) => {
          const orderNum = Number(i.displayorder);
          if (!isNaN(orderNum) && orderNum > 0) {
            allOrders.push(orderNum);
          }
        });
      }

      if (Array.isArray(rawChecklistData)) {
        rawChecklistData.forEach((i: any) => {
          if (Number(i.checklisttype) === typeVal) {
            const orderNum = Number(i.displayorder);
            if (!isNaN(orderNum) && orderNum > 0) {
              allOrders.push(orderNum);
            }
          }
        });
      }

      const maxOrder = allOrders.length > 0 ? Math.max(...allOrders) : 0;
      const nextOrder = String(maxOrder + 1).padStart(2, "0");

      const newItem = {
        masterchecklistid: Date.now(),
        question: record.question,
        checklisttype: typeVal,
        checklisttypetext: selectedTypeLabel,
        checklistcategory: categoryVal,
        isrequired: record.isrequired,
        assigned: true,
        isCustom: true,
        displayorder: nextOrder,
      };
      setCustomChecklists((prev) => ({
        ...prev,
        [currentKey]: [...currentChecklists, newItem],
      }));
      showToastMessage("success", "Checklist added");
    }

    setModalOpen(false);
  };

  useEffect(() => {
    if (setValue) {
      setValue("configuredChecklistsData", {
        assignedState,
        customChecklists,
        hiddenTypeValuesState,
        isEditMode,
        existingStudyChecklists,
      });
    }
  }, [assignedState, customChecklists, hiddenTypeValuesState, isEditMode, existingStudyChecklists, setValue]);

  const modalPreSelectedType = useMemo(() => {
    if (!selectedType) return null;
    const match = activeChecklistTypes.find(
      (opt: any) => Number(opt.value) === Number(selectedType)
    );
    if (match) {
      return match;
    }
    return {
      label: selectedTypeLabel,
      value: Number(selectedType),
    };
  }, [activeChecklistTypes, selectedType, selectedTypeLabel]);

  const modalExistingData = useMemo(() => {
    if (!editingItem) return null;
    return {
      ...editingItem,
      checklisttype: editingItem.checklisttype || selectedType,
    };
  }, [editingItem, selectedType]);

  return (
    <div>
      {/* ── Select Checklist Scope Cards (when Inclusion/Exclusion exist) ── */}
      {hasInclusionExclusionCriteria && (
        <div className="mb-4">
          <div className="fw-semibold text-dark mb-2 fs-14">Select Checklist Scope</div>
          <div className="d-flex flex-wrap gap-3">
            <div
              onClick={() => setSelectedScope("pre_enrollment")}
              className={selectedScope === "pre_enrollment" ? styles.scopeCardSelected : styles.scopeCardBase}
              style={{ minWidth: "270px" }}
            >
              <div
                className={styles.scopeIconWrapper}
                style={{
                  backgroundColor: selectedScope === "pre_enrollment" ? "#dbeafe" : "#f1f5f9",
                  color: selectedScope === "pre_enrollment" ? "#0b5ed7" : "#64748b",
                }}
              >
                <i className="ri-file-text-line" />
              </div>
              <div>
                <h6 className="mb-0 fw-bold fs-14 text-dark">Enrollment Checklists</h6>
                <p className="mb-0 text-muted fs-12">Applies before subject enrollment</p>
              </div>
            </div>

            <div
              onClick={() => setSelectedScope("period_based")}
              className={selectedScope === "period_based" ? styles.scopeCardSelected : styles.scopeCardBase}
              style={{ minWidth: "270px" }}
            >
              <div
                className={styles.scopeIconWrapper}
                style={{
                  backgroundColor: selectedScope === "period_based" ? "#dbeafe" : "#f1f5f9",
                  color: selectedScope === "period_based" ? "#0b5ed7" : "#64748b",
                }}
              >
                <i className="ri-calendar-line" />
              </div>
              <div>
                <h6 className="mb-0 fw-bold fs-14 text-dark">Study Period Checklists</h6>
                <p className="mb-0 text-muted fs-12">Applies to specific study periods/visits</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Period Selector Chips (Only when Period-Based scope selected or no Inclusion/Exclusion) ── */}
      {(!hasInclusionExclusionCriteria || selectedScope === "period_based") && (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-14">Select Period</div>
          <HorizontalChipBar id="period-chips">
            {periodList.map((p) => {
              const isSelected = selectedPeriod === p;
              return (
                <div
                  key={p}
                  onClick={() => {
                    setSelectedPeriod(p);
                    setShowCopyDropdown(false);
                  }}
                  className={`${styles.periodChipBase} ${isSelected ? styles.periodChipSelected : ""}`}
                >
                  <i
                    className={`${isSelected ? "ri-checkbox-circle-fill" : "ri-calendar-line"} me-2`}
                    style={{ fontSize: "15px" }}
                  />
                  Period {p}
                </div>
              );
            })}
          </HorizontalChipBar>
        </div>
      )}

      {/* Checklist Type Chips with X / + control */}
      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="fw-medium text-muted fs-14">Select Checklist Type</span>

          {/* ＋ button — in header row */}
          <div className="position-relative" ref={addTypeDropdownRef}>
            <button
              type="button"
              title={hiddenChecklistTypes.length === 0 ? "No hidden types" : "Add back hidden checklist types"}
              disabled={hiddenChecklistTypes.length === 0}
              onClick={() => setShowAddTypeDropdown((prev) => !prev)}
              className={styles.addTypeHeaderBtn}
            >
              <i className="ri-add-line" style={{ fontSize: "14px" }} />
              {hiddenChecklistTypes.length > 0 && (
                <span className={styles.badgeCounter}>{hiddenChecklistTypes.length}</span>
              )}
            </button>

            {/* Hidden types dropdown */}
            {showAddTypeDropdown && hiddenChecklistTypes.length > 0 && (
              <div className={styles.dropdownPopover}>
                <div className={styles.dropdownHeader}>Hidden Types</div>
                {hiddenChecklistTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleRestoreType(type.value)}
                    className={styles.dropdownItemBtn}
                  >
                    <i className="ri-file-list-3-line text-primary fs-15 flex-shrink-0" />
                    <div className="flex-grow-1 overflow-hidden">
                      <TruncatedText text={type.label} maxWidth="140px" />
                    </div>
                    <span className="text-muted fs-11 ms-auto flex-shrink-0">
                      ({getTypeCount(type.value)})
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Scrollable chip row */}
        <HorizontalChipBar id="checklist-type-chips">
          {visibleChecklistTypes.map((type) => {
            const count = getTypeCount(Number(type.value));
            const isSelected = Number(selectedType) === Number(type.value);
            return (
              <div
                key={type.value}
                className={`${styles.chipBase} ${isSelected ? styles.chipSelected : ""}`}
              >
                <span
                  onClick={() => setSelectedType(Number(type.value))}
                  className="d-inline-flex align-items-center gap-1"
                >
                  <i
                    className={`${isSelected ? "ri-checkbox-circle-fill" : "ri-file-list-3-line"}`}
                    style={{ fontSize: "15px" }}
                  />
                  {type.label} ({count})
                </span>

                {/* ✕ button */}
                <button
                  type="button"
                  title={`Hide ${type.label}`}
                  className={styles.chipCloseBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHideType(Number(type.value));
                  }}
                >
                  <i className="ri-close-line" style={{ fontSize: "13px" }} />
                </button>
              </div>
            );
          })}
        </HorizontalChipBar>
      </div>

      {visibleChecklistTypes.length === 0 || !selectedType ? (
        <Card className="border shadow-sm mb-4">
          <CardBody className="p-4 text-center text-muted">
            <i className="ri-file-list-3-line fs-1 text-secondary mb-2 d-block" style={{ fontSize: "2rem" }} />
            <p className="mb-1 fw-medium fs-15 text-secondary">No checklist types are visible</p>
            <small className="text-muted">
              {hiddenChecklistTypes.length > 0
                ? "Click the '+' button above to restore hidden checklist types."
                : "No checklist types available."}
            </small>
          </CardBody>
        </Card>
      ) : (
        <Card className="border shadow-sm mb-4">
          <CardBody className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2 small">
                {hasInclusionExclusionCriteria && selectedScope === "pre_enrollment" ? (
                  <>
                    <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-semibold fs-12">
                      Enrollment Checklists
                    </span>
                    <i className="ri-arrow-right-s-line text-muted"></i>
                  </>
                ) : (
                  <>
                    <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-semibold fs-12">
                      Period {selectedPeriod}
                    </span>
                    <i className="ri-arrow-right-s-line text-muted"></i>
                  </>
                )}
                <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-semibold fs-12">
                  {selectedTypeLabel}
                </span>
              </div>

              {/* Action buttons */}
              <div className="d-flex gap-2 align-items-center flex-wrap">
                {(!hasInclusionExclusionCriteria || selectedScope === "period_based") && (
                  <div className="position-relative" ref={copyDropdownRef}>
                    <Button
                      type="button"
                      color="secondary"
                      outline
                      size="sm"
                      disabled={numPeriods <= 1}
                      title={numPeriods <= 1 ? "No other periods available" : "Copy checklists from another period"}
                      onClick={() => setShowCopyDropdown((prev) => !prev)}
                    >
                      <i className="ri-file-copy-line me-1" />
                      Copy From Period
                    </Button>

                    {showCopyDropdown && (
                      <div className={styles.dropdownPopover}>
                        <div className={styles.dropdownHeader}>
                          Copy <span className="text-primary">{selectedTypeLabel}</span> from:
                        </div>

                        {periodList
                          .filter((p) => p !== selectedPeriod)
                          .map((p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => handleCopyFromPeriod(p)}
                              className={styles.dropdownItemBtn}
                            >
                              <i className="ri-calendar-line text-primary fs-14" />
                              Period {p}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                <Button
                  type="button"
                  color="primary"
                  outline
                  size="sm"
                  onClick={handleOpenAddModal}
                >
                  <i className="ri-add-line me-1" /> Add Checklist
                </Button>
              </div>
            </div>

            <div className={styles.checklistTableWrapper}>
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={currentChecklists.map((item: any) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <CommonTableIndexWise
                    moduleName={PERMISSION_MODULES.STUDY}
                    columns={checkliststepcolumns(isAllAssigned, currentChecklists, handleToggleSelectAll, handleToggleAssign)}
                    data={currentChecklists}
                    paginationShow={false}
                    showStatus={false}
                    loading={!!checklistLoading}
                    dragAndDrop
                    actions={{
                      onEdit: (row: any) => handleOpenEditModal(row),
                      // onDelete: (row: any) => {
                      //   const targetId = row.id || row.masterchecklistid;
                      //   setCustomChecklists((prev) => ({
                      //     ...prev,
                      //     [currentKey]: currentChecklists.filter(
                      //       (i: any) => (i.id || i.masterchecklistid) !== targetId
                      //     ),
                      //   }));
                      // },
                    }}
                  />
                </SortableContext>
              </DndContext>
            </div>
          </CardBody>
        </Card>
      )}

      <AddEditCheckListModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        existingData={modalExistingData}
        preSelectedType={modalPreSelectedType}
        onSave={handleSaveModal}
        isLoading={false}
      />
    </div>
  );
};

export default ChecklistAssignmentStep;
